import React, {useEffect, useState} from "react";
import {
    AccountingAccountList,
    AccountingAccountRestControllerApiFactory,
    BankCounterAccountSuggestion,
    BankJournalCandidateDto,
    BankStatementLineList,
    BankStatementRestControllerApiFactory,
} from "arteco-api-client-ts";
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import {CheckCircleRounded, ManageSearchRounded} from "@mui/icons-material";
import {useApiManager} from "../../../../components/utils/api";
import {fmtAmt} from "../../../../components/utils/numbers";
import {dateToString} from "../../../../components/utils/dates";
import {accountLabel, postableAccounts, renderAccountOption} from "../../../../components/utils/accounts";
import {btnSx, EmptyState, ListCard, SkeletonRows, SoftChip} from "../../../../components/kit";

/** Importe con su signo en color: verde lo que entra, rojo lo que sale. */
export const SignedAmount = ({amount}: { amount?: number }) => (
    <Box component="span" sx={{
        color: (amount ?? 0) >= 0 ? "success.main" : "error.main",
        fontWeight: 700, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap",
    }}>
        {fmtAmt(amount)}
    </Box>
);

/** La línea del extracto de la que se habla, en una franja: fecha, concepto e importe. */
const LineSummary = ({line}: { line: BankStatementLineList }) => (
    <Box sx={{p: 1.5, mb: 2, borderRadius: 2, bgcolor: "action.hover", display: "flex", gap: 2, alignItems: "baseline", flexWrap: "wrap"}}>
        <Typography variant="body2" sx={{fontVariantNumeric: "tabular-nums"}}>{dateToString(line.operationDate ?? line.valueDate)}</Typography>
        <Typography variant="body2" sx={{flex: 1, minWidth: 0, wordBreak: "break-word"}}>{line.description ?? "-"}</Typography>
        <SignedAmount amount={line.amount}/>
    </Box>
);

/**
 * Apuntes del diario que casan con una línea del extracto (#14706): misma cuenta del banco, mismo importe
 * con signo y a ±3 días. Se elige uno y queda conciliado; si no hay ninguno, se ofrece asentarla.
 */
export const JournalCandidatesDialog = ({line, onClose, onReconciled, onPost}: {
    line: BankStatementLineList | null,
    onClose: () => void,
    onReconciled: () => void,
    onPost: (line: BankStatementLineList) => void,
}) => {
    const apiManager = useApiManager();
    const [candidates, setCandidates] = useState<BankJournalCandidateDto[] | null>(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setCandidates(null);
        if (!line?.id) return;
        const ctrl = apiManager.factory(BankStatementRestControllerApiFactory);
        apiManager.execute(ctrl.listJournalCandidatesBankStatementLine(line.id))
            .then(setCandidates)
            .catch(() => setCandidates([]));
    }, [line?.id]);

    const reconcile = (candidate: BankJournalCandidateDto) => {
        if (!line?.id || !candidate.journalLineId) return;
        setSaving(true);
        const ctrl = apiManager.factory(BankStatementRestControllerApiFactory);
        apiManager.execute(ctrl.reconcileWithJournalBankStatementLine(line.id, candidate.journalLineId), "Conciliada con el diario")
            .then(onReconciled)
            .catch(() => {})
            .finally(() => setSaving(false));
    };

    return (
        <Dialog open={!!line} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle sx={{fontWeight: 700}}>Buscar en el diario</DialogTitle>
            <DialogContent>
                {line && <LineSummary line={line}/>}
                {candidates === null ? (
                    <SkeletonRows rows={3} height={64}/>
                ) : candidates.length === 0 ? (
                    <EmptyState icon={<ManageSearchRounded/>} title="Ningún apunte del diario casa con esta línea"
                                description="Se buscan asientos confirmados en la cuenta del banco con el mismo importe y a ±3 días que no estén ya conciliados."
                                action={line ? <Button variant="contained" onClick={() => onPost(line)} sx={btnSx}>Asentar la línea</Button> : undefined}/>
                ) : (
                    <Stack spacing={1}>
                        {candidates.map(c => (
                            <ListCard key={c.journalLineId}>
                                <Box sx={{display: "flex", gap: 1.5, alignItems: {xs: "flex-start", sm: "center"}, flexDirection: {xs: "column", sm: "row"}}}>
                                    <Box sx={{flex: 1, minWidth: 0}}>
                                        <Typography variant="body2" sx={{fontWeight: 600, wordBreak: "break-word"}}>
                                            {c.entryDescription ?? "Sin concepto"}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{fontVariantNumeric: "tabular-nums"}}>
                                            {dateToString(c.entryDate)} · asiento {c.entryNumber ?? "sin número"} · cuenta {c.accountCode}
                                        </Typography>
                                    </Box>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <SoftChip label={c.daysApart === 0 ? "mismo día" : `${c.daysApart} d`} tone={c.daysApart === 0 ? "success" : "neutral"}/>
                                        <SignedAmount amount={c.amount}/>
                                        <Button size="small" variant="contained" color="success" startIcon={<CheckCircleRounded/>}
                                                disabled={saving} onClick={() => reconcile(c)} sx={btnSx}>
                                            Conciliar
                                        </Button>
                                    </Stack>
                                </Box>
                            </ListCard>
                        ))}
                    </Stack>
                )}
            </DialogContent>
            <DialogActions sx={{px: 3, pb: 2}}>
                {line && candidates && candidates.length > 0 && (
                    <Button onClick={() => onPost(line)} sx={{...btnSx, mr: "auto"}}>Ninguno: asentarla</Button>
                )}
                <Button onClick={onClose} sx={btnSx}>Cerrar</Button>
            </DialogActions>
        </Dialog>
    );
};

/**
 * Asentar una línea del extracto (#14706): un asiento confirmado banco contra la contrapartida que se elija,
 * con el concepto del extracto, que queda conciliado con la línea.
 */
export const PostEntryDialog = ({line, onClose, onPosted}: {
    line: BankStatementLineList | null,
    onClose: () => void,
    onPosted: () => void,
}) => {
    const apiManager = useApiManager();
    const [account, setAccount] = useState<AccountingAccountList | null>(null);
    const [accountInput, setAccountInput] = useState("");
    const [options, setOptions] = useState<AccountingAccountList[]>([]);
    const [description, setDescription] = useState("");
    const [suggestion, setSuggestion] = useState<BankCounterAccountSuggestion | null>(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setAccount(null);
        setAccountInput("");
        setSuggestion(null);
        setDescription(line?.description ?? "");
        if (!line?.id) return;
        const ctrl = apiManager.factory(BankStatementRestControllerApiFactory);
        apiManager.execute(ctrl.suggestCounterAccountBankStatementLine(line.id))
            .then(s => {
                if (!s?.accountId) return;
                setSuggestion(s);
                setAccount({id: s.accountId, code: s.code, name: s.name} as AccountingAccountList);
            })
            .catch(() => {});
    }, [line?.id]);

    const search = (term: string) => {
        const ctrl = apiManager.factory(AccountingAccountRestControllerApiFactory);
        apiManager.execute(ctrl.listAccountingAccount(undefined, undefined, undefined, undefined, undefined, undefined, term, false))
            .then(list => setOptions(postableAccounts(list as any)))
            .catch(() => {});
    };

    const post = () => {
        if (!line?.id || !account?.id) return;
        setSaving(true);
        const ctrl = apiManager.factory(BankStatementRestControllerApiFactory);
        apiManager.execute(ctrl.postStatementLineEntryBankStatementLine(line.id, {
            counterAccountId: account.id,
            description: description.trim() || undefined,
        }), "Asiento creado y línea conciliada")
            .then(onPosted)
            .catch(() => {})
            .finally(() => setSaving(false));
    };

    const incoming = (line?.amount ?? 0) >= 0;

    return (
        <Dialog open={!!line} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle sx={{fontWeight: 700}}>Asentar la línea</DialogTitle>
            <DialogContent>
                {line && <LineSummary line={line}/>}
                <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                    Se crea un asiento confirmado con fecha de operación: {incoming
                    ? "el banco al debe y la contrapartida al haber, porque entra dinero"
                    : "la contrapartida al debe y el banco al haber, porque sale dinero"}. La línea queda conciliada con él.
                </Typography>
                {suggestion?.accountId && (
                    <Alert severity="info" sx={{mb: 2, borderRadius: 2}}>
                        Propuesta: {suggestion.reason}. Cámbiala si no es esta.
                    </Alert>
                )}
                <Stack spacing={2}>
                    <Autocomplete
                        options={options}
                        value={account}
                        inputValue={accountInput}
                        onInputChange={(_, val) => {
                            setAccountInput(val);
                            if (val.length >= 2) search(val);
                        }}
                        onChange={(_, val) => setAccount(val)}
                        getOptionLabel={(o) => accountLabel(o)}
                        isOptionEqualToValue={(a, b) => a.id === b.id}
                        filterOptions={(x) => x}
                        renderOption={(props, option) => renderAccountOption(props, option as any)}
                        renderInput={(params) => (
                            <TextField {...params} label="Cuenta contrapartida" size="small" fullWidth
                                       helperText="Escribe el código o el nombre: cliente, proveedor, otro banco, gastos…"/>
                        )}
                        noOptionsText={accountInput.length < 2 ? "Escribe al menos dos caracteres" : "Sin cuentas que admitan apuntes"}
                    />
                    <TextField label="Concepto del asiento" size="small" fullWidth value={description}
                               onChange={e => setDescription(e.target.value)} inputProps={{maxLength: 500}}/>
                </Stack>
            </DialogContent>
            <DialogActions sx={{px: 3, pb: 2}}>
                <Button onClick={onClose} sx={btnSx}>Cancelar</Button>
                <Button variant="contained" onClick={post} disabled={saving || !account?.id} sx={btnSx}>
                    {saving ? "Asentando..." : "Asentar"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
